import React, { useEffect } from "react";
import { Form, Button, Input, Select, Upload } from "antd";
import { DataProvider } from "../../../DataProvider";
import { openNotification } from "swfrontend/COMS/NotificationMessageMapping";
import { UploadOutlined } from "@ant-design/icons";
import { ACCEPT_FILE_TYPES, beforeUpload, NEW_UPDATE_FILE_KEYS } from "../../../Constants";

import MultipartDataUploader from "../../NewClaim/MultipartDataUploader";

const NewUpdate = ({
  disabled = false,
  updateData = {},
  contractId = null,
}) => {
  const dataProvider = new DataProvider();
  const [form] = Form.useForm();
  const [fileUploaded, setFileUploaded] = React.useState(false);

  const STATUS_OBJ = [
    { value: "open", label: "OPEN" },
    { value: "responded", label: "RESPONDED" },
    { value: "settled", label: "SETTLED" },
    { value: "refuted", label: "REFUTED" },
  ];

  const handleFileUpload = (name, value) => {
    const file = value?.file?.originFileObj || value?.file;

    if (file && !beforeUpload(file)) {
      return;
    }
    const allFields = form.getFieldsValue();
    const fileCount = Object.keys(allFields).filter((key) =>
      key.startsWith(name)).length;
    const newName = `${name}_${fileCount + 1}`;
    // Use originFileObj to get the binary file
    form.setFieldsValue({ [newName]: file });
    setFileUploaded(true);
  };

  useEffect(() => {
    if (Object.keys(updateData).length > 0) {
      form.setFieldsValue({ ...updateData, contract: contractId });
    } else {
      form.setFieldsValue({ contract: contractId });
    }
  }, [updateData, contractId, form]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    form.setFieldsValue({ [name]: value });
  };

  const updateStatus = (value) => {
    form.setFieldsValue({ status: value });
  };

  const FormSubmission = () => {
    form
      .validateFields()
      .then((values) => {
        const multipartData = new FormData();

        // Populate the FormData with all values from the form
        Object.entries(values).forEach(([key, value]) => {
          if (NEW_UPDATE_FILE_KEYS.includes(key) && value?.fileList && value.fileList.length > 0) {
            value.fileList.forEach((file, index) => {
              multipartData.append(`${key}_${index + 1}`, file.originFileObj);
            });
            delete values[key];
          } else {
            multipartData.append(key, value);
          }
        });

        if (fileUploaded) {
          const url = "/claim/discussion/";

          MultipartDataUploader(url, multipartData, "POST", true, true)
            .then((response) => {
              if (response.status == 201) {
                openNotification(
                  "success",
                  "Success",
                  "Update Inserted Successfully"
                );
                setTimeout(() => window.location.reload(), 1000);
              } else {
                openNotification(
                  "error",
                  "Error",
                  "Error Occoured while creating update"
                );
              }
            })
            .catch((exception) => {
              console.error(
                "Error Occurred While Uploading Multipart Data:",
                exception
              );
            });
        } else {
          dataProvider.newDiscussionCreate(values).then((response) => {
            if (response.ok) {
              response.json().then((data) => {
                openNotification("success", "Success", data["message"]);
                setTimeout(() => window.location.reload(), 1000);
              });
            } else {
              response.json().then((responseJson) => {
                openNotification("error", "Error", responseJson["message"]);
              });
            }
          });
        }
      })
      .catch((errorInfo) => {
        console.error("Validation Failed:", errorInfo);
      });
  };

  const getUploadList = (name) => {
    return Object.keys(form)
      .filter((key) => key.startsWith(name))
      .map((key) => ({
        uid: key,
        name: form[key].name,
        status: "done",
        url: URL.createObjectURL(form[key]),
      }));
  };

  return (
    <div>
      <div className="row form-box" style={{ paddingTop: "10px" }}>
        <Form
          className="ant-form ant-form-vertical"
          style={{ width: "100%" }}
          form={form}
        >
          <legend className="form-legend">Claim Update</legend>

          <Form.Item name="contract" hidden>
            <Input />
          </Form.Item>

          <div className="col-lg-12 col-md-12" style={{ display: "flex" }}>
            <Form.Item
              name="file"
              label="Supporting Documents"
              rules={[
                {
                  required: true,
                  message: "Please attach a support document!",
                },
              ]}
              style={{ width: "45%", marginLeft: "1%" }}
            >
              <Upload
                onChange={(info) => handleFileUpload("file", info)}
                showUploadList={{
                  showRemoveIcon: true,
                  items: getUploadList("file"),
                }}
                beforeUpload={(file) => {
                  // Handle file upload manually
                  beforeUpload("file", { file });
                  // Prevent automatic form submission
                  return false;
                }}
                maxCount={5}
                accept={ACCEPT_FILE_TYPES}
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Please select a status!" }]}
              style={{ width: "45%", marginLeft: "5%" }}
            >
              <Select
                allowClear
                showSearch
                placeholder="Select Status"
                disabled={disabled}
                onSelect={updateStatus}
                options={STATUS_OBJ.map((val) => ({
                  value: val.value,
                  label: val.label,
                }))}
              />
            </Form.Item>
          </div>

          <div className="col-lg-12 col-md-12" style={{ display: "flex" }}>
            <Form.Item
              name="updates"
              label="Summary"
              rules={[
                {
                  required: true,
                  message: "Please provide an update summary!",
                },
              ]}
              style={{ width: "45%", marginLeft: "1%" }}
            >
              <Input
                placeholder="Enter Update Summary"
                style={{ width: "100%" }}
                name="updates"
                disabled={disabled}
                onBlur={handleChange}
              />
            </Form.Item>
          </div>

          <div className="col-lg-12 col-md-12" style={{ display: "flex" }}>
            <Form.Item
              name={"letter_number"}
              label="Letter Number"
              rules={[{ required: false }]}
              style={{ width: "92%", marginLeft: "1%" }}
            >
              <Input
                placeholder="Enter Letter Number"
                style={{ width: "100%" }}
                name="letter_number"
                disabled={disabled}
                onBlur={handleChange}
                maxLength={100}
              />
            </Form.Item>

            <Form.Item
              name="letter_file"
              label="Letter"
              rules={[
                {
                  required: false,
                },
              ]}
              style={{ width: "100%", marginLeft: "5%" }}
            >
              <Upload
                onChange={(info) => handleFileUpload("letter_file", info)}
                showUploadList={{
                  showRemoveIcon: true,
                  items: getUploadList("contractor_file"),
                }}
                beforeUpload={(file) => {
                  // Handle file upload manually
                  beforeUpload("letter_file", { file });
                  // Prevent automatic form submission
                  return false;
                }}
                // beforeUpload={beforeUpload}
                maxCount={1}
                accept={ACCEPT_FILE_TYPES}
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
          </div>

          <div
            style={{ textAlign: "left", marginLeft: "2%", marginBottom: "1%" }}
          >
            <Button
              htmlType="submit"
              type="primary"
              onClick={FormSubmission}
              disabled={disabled}
              hidden={disabled}
            >
              Submit
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default NewUpdate;
